import { memo, useEffect, useRef, useState } from 'react'
import { useCountdown } from 'usehooks-ts'
import { PhotoCameraIcon } from '../../../../components/Icons'

import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { useWebcam } from '../../../../hooks/useWebCam'
import { BackBtn } from '../BackBtn'
import { Heading } from '../Heading'
import { MediaPermissionsError } from '../MediaPermissionsError'
interface CameraUiProps {
  setUrl: (url: string | ArrayBuffer | null) => void
  setTabName: (name: string) => void
}

const CameraUi: React.FC<CameraUiProps> = memo(({ setUrl, setTabName }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const { setIsCameraReady } = useTwinData()

  const [isCountdownActive, setIsCountdownActive] = useState(false)
  const { takePhoto, ready, hasWebCamPermission } = useWebcam(videoRef)

  const [count, { startCountdown }] = useCountdown({
    countStart: 3,
    intervalMs: 1000,
  })

  const handleTakePhoto = async () => {
    await takePhoto().then(photo => {
      setUrl(photo?.dataUrl || null)
      setTabName('preview')
    })
  }

  const handleStopCamera = () => {
    setTabName('')
  }

  const handleRunCountdown = () => {
    setIsCountdownActive(true)
    startCountdown()
  }

  useEffect(() => {
    setIsCameraReady(ready)
    return () => {
      setIsCameraReady(false)
    }
  }, [ready])

  useEffect(() => {
    if (count === 0) {
      void handleTakePhoto()
    }
  }, [count, handleTakePhoto])

  // Show permission error if camera access is denied
  if (hasWebCamPermission === false) {
    return (
      <MediaPermissionsError
        title="Camera Access Required"
        description="We need access to your camera to take your selfie. Please enable camera permissions in your browser settings to continue."
        instruction={[
          'Click the camera icon in your browser’s address bar',
          'Select Allow for camera',
          'Refresh the page if needed',
        ]}
      />
    )
  }

  return (
    <div
      className={`z-[51] w-full h-full sm:w-[750px] sm:max-w-full mx-auto ${ready ? 'fixed' : ''} top-0 left-0 sm:relative overflow-hidden mb-auto sm:mb-0`}
    >
      <div className="relative mt-8  sm:mt-0 z-10 w-full">
        <Heading
          title="Take a Selfie"
          subtitle={
            <>
              <span className="hidden sm:block">
                Natural expression with your mouth closed, position your face
                within silhouette to get the perfect shot.
              </span>
              <span className="block sm:hidden px-4">
                Natural expression with your mouth closed, center your face
                within the screen to get the perfect shot.
              </span>
            </>
          }
        />
      </div>
      {!ready && (
        <div className="relative top-0 sm:absolute w-full sm:top-1/2 flex items-center justify-center z-50">
          <div className="text-white text-center">
            <AnimatedSpinner />
            <p>Connecting to camera...</p>
          </div>
        </div>
      )}

      <div className="absolute h-full sm:h-auto top-0 left-0 w-full sm:rounded-2xl aspect-video max-w-full mx-auto sm:relative overflow-hidden mb-auto sm:mb-0">
        {isCountdownActive && count > 0 && (
          <div className="absolute z-50 inset-0 flex items-center justify-center pointer-events-none text-[120px] font-medium">
            {count}
          </div>
        )}

        {!ready ? (
          <></>
        ) : (
          <>
            <BackBtn onClick={handleStopCamera} />
            <div className="absolute -bottom-[10%] top-[20%] left-1/2 -translate-x-1/2 w-full flex justify-center pointer-events-none z-10">
              <img
                src="/images/selfie-frame.png"
                alt="Frame"
                className="w-auto h-full hidden sm:block"
              />
            </div>
          </>
        )}

        <video
          className="w-full h-full object-cover sm:rounded-2xl -scale-x-100"
          autoPlay
          playsInline
          muted
          disablePictureInPicture
          ref={videoRef}
        />
        {/* Gradient background */}
        <div className="sm:hidden block absolute inset-0 w-full h-82 bg-gradient-to-b from-black/40 to-transparent z-1" />
      </div>
      {ready && (
        <div className="absolute bottom-12 sm:bottom-0 sm:relative sm:flex items-center justify-center gap-4 flex-wrap mt-8 z-10 left-1/2 transform -translate-x-1/2">
          <button
            data-testid="camera-take-selfie-btn"
            className="btn btn-primary"
            onClick={handleRunCountdown}
            disabled={isCountdownActive}
          >
            <PhotoCameraIcon size={16} />
            <span className="ml-2">Take a Selfie</span>
          </button>
        </div>
      )}
    </div>
  )
})

export { CameraUi }
